import {Component, computed, effect, inject, input, signal} from '@angular/core';
import {SettingsHttpService} from "../_services/settings-http.service";
import {LegalProviderModel} from "../_models/settings/legal-provider-model";
import {LegalProvider, LegalProviderHelper} from "../_models/_enums/legal-provider";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {LegalType, LegalTypeHelper} from "../_models/_enums/legal-type";
import {LegalAccountState, LegalAccountStateHelper} from "../_models/_enums/legal-account-state";
import {describeAdministrationError} from "../../../_shared/errors/administration-errors";
import {LegalReadonlyHintComponent} from "../_shared/legal-readonly-hint.component";
import {ProviderSwitchConfirmDialogComponent} from "./provider-switch-confirm-dialog.component";

/**
 * The sentence for a provider switch that failed for a reason this build has no code for — a network failure, a
 * 500, an older server still answering the bare `ResponseErrors` ordinal.
 *
 * Deliberately kept as the FALLBACK only. Until #629 it was the entire error copy of this screen: both real
 * refusals (a registered device, an open cash point) carried no `ApiError` at all, so a state the operator could
 * have fixed in a minute read exactly like an outage.
 */
export const PROVIDER_SELECTION_FALLBACK = 'Provider konnte nicht ausgewählt werden.';

/**
 * The sentence for a credential save that fails for a reason this build has no code for — a network failure, a
 * 500, an older server still answering the bare `ResponseErrors` ordinal.
 *
 * Kept as the fallback only (#896): saving „Key"/„Secret" is now refused by the same two guards the provider
 * switch has answered since #629 — a registered device, an open cash point — and reuses their codes and German
 * sentences ({@link PROVIDER_SELECTION_FALLBACK} has the fuller history). Both refusals apply whether the save
 * also changes the provider or only renews the credentials of the one already in force.
 */
export const CREDENTIALS_SAVE_FALLBACK = 'Accountdaten konnten nicht gespeichert werden.';

/**
 * What a **verified but rejected** save says (#896).
 *
 * This is not the {@link CREDENTIALS_SAVE_FALLBACK}/refusal-code path above — those are the request never
 * reaching verification at all. This is the request that DID reach the provider, and the provider said no.
 * The server no longer stores a pair it cannot verify, so the editor has to stay open and say so in words
 * rather than closing as if the save had gone through: a closed editor plus a red chip reads as "saved, but
 * now broken" when what actually happened is "not saved at all".
 */
export const CREDENTIALS_REJECTED_MESSAGE =
  'Der Provider hat die eingegebenen Zugangsdaten abgelehnt. Sie wurden nicht gespeichert. Bitte prüfen Sie '
  + 'Key und Secret und versuchen Sie es erneut.';

/** The switch the reader has asked for and not yet confirmed — what the confirm dialog is about. */
interface PendingProviderSwitch {
  provider: LegalProvider;
  setting: LegalProviderModel;
}

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  imports: [
    LegalReadonlyHintComponent,
    ProviderSwitchConfirmDialogComponent
  ],
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent {
  private readonly settingsHttpService = inject(SettingsHttpService);
  private readonly globalAlertService = inject(GlobalAlertService);

  settings: LegalProviderModel[] = [];
  protected readonly LegalProviderHelper = LegalProviderHelper;
  protected readonly LegalTypeHelper = LegalTypeHelper;
  protected readonly LegalProvider = LegalProvider;
  protected readonly LegalAccountState = LegalAccountState;
  protected readonly LegalAccountStateHelper = LegalAccountStateHelper;

  /**
   * (#633) Set only when this instance is embedded in the OWNER area for a specific shard
   * (**Owner → Rechtliches**, `_modules/owner/legal/`) — never on the tenant route `/legal/settings`. A
   * `PlatformOwner` who has entered a tenant context loses the `PlatformOwner` role by design (context
   * narrowing), so a permission check here could never be reached from the tenant route in the first place;
   * the owner must always come through the OWNER area instead, where the shard id is explicit.
   * `PermissionService` is deliberately no longer a dependency of this component.
   */
  readonly shardId = input<string | undefined>();

  /**
   * OWNER MODE (full mutating controls, HTTP calls scoped to {@link shardId}) vs TENANT MODE (read-only for
   * everyone, incl. a PlatformOwner viewing a shard's `/legal/settings` — see the class doc on {@link shardId}).
   */
  protected readonly isOwner = computed(() => this.shardId() !== undefined);

  /** Open while the reader is being asked to type „BESTÄTIGEN" for a switch. */
  protected readonly pendingSwitch = signal<PendingProviderSwitch | undefined>(undefined);
  protected readonly switching = signal(false);

  constructor() {
    // Load in an EFFECT, not in `ngOnInit` (#631): the owner page keeps this component mounted and swaps the
    // picked shard, so a once-only `ngOnInit` load left the previous location's providers on screen under the
    // new location's heading — the worst possible failure on a screen whose actions are irreversible.
    effect(() => {
      this.shardId();
      this.load();
    });
  }

  /**
   * Asks for the switch. The typed confirmation lives in the dialog, so this only opens it —
   * {@link confirmSwitch} is what talks to the server.
   */
  selected(provider: LegalProvider, type: LegalProviderModel) {
    // Defence in depth: the template only renders the switch control in owner mode, but the method itself must
    // also refuse — matches `TechnicalSecuritySystemComponent`'s guarded mutators (#613/#633).
    if (!this.isOwner()) return;
    if (type.legalProvider == provider) return;

    this.pendingSwitch.set({provider, setting: type});
  }

  protected cancelSwitch(): void {
    this.pendingSwitch.set(undefined);
  }

  /** The dialog's „Ja, Anbieter wechseln" — the single place `SelectProvider` is called. */
  protected confirmSwitch(): void {
    const pending = this.pendingSwitch();
    // Defence in depth again: the one chokepoint the confirmed path funnels through.
    if (!pending || !this.isOwner()) return;

    const {provider, setting} = pending;
    this.switching.set(true);

    this.settingsHttpService.selectProvider(setting.legalType, provider, this.shardId()).subscribe({
      next: (response) => {
        this.switching.set(false);
        this.pendingSwitch.set(undefined);
        setting.legalProvider = provider;
        setting.legalAccountState = response;
      },
      error: (error) => {
        this.switching.set(false);
        this.pendingSwitch.set(undefined);
        console.error(error)
        // Branch on `error.code`, never render the backend's English `description` (style guide, "Surfacing an
        // API error"). The two codes this endpoint really answers — a registered device and an open cash point
        // (#629) — each name the state the operator has to undo; anything else degrades to the generic
        // sentence rather than to the raw field.
        this.globalAlertService.createAndShow("Fehler",
          describeAdministrationError(error, PROVIDER_SELECTION_FALLBACK), AlertLevel.error, 8000);
      }
    })
  }

  /** Opens the Key/Secret editor of one area („Zugangsdaten" / „Beheben"). */
  protected edit(setting: LegalProviderModel): void {
    if (!this.isOwner()) return;
    setting.isEditing = true;
  }

  protected cancelEdit(setting: LegalProviderModel): void {
    setting.isEditing = false;
  }

  /** The editor's submit. Native `submit`, so Enter in either field saves — hence the `preventDefault`. */
  protected submitCredentials(event: Event, setting: LegalProviderModel, key: string, secret: string): void {
    event.preventDefault();
    this.setCredentials(setting, key, secret);
  }

  setCredentials(setting: LegalProviderModel, key: string, secret: string) {
    // Defence in depth — see the same guard in `selected()`.
    if (!this.isOwner()) return;

    this.settingsHttpService
      .setCredentials(key, secret, setting.legalType, this.shardId())
      .withLoadingIndicator()
      .subscribe({
        next: (response) => {
          setting.legalAccountState = response;
          // Close only on an ACCEPTED pair (#896). The server verifies before it writes anything, so a
          // rejection is a 200 carrying `CredentialsUnauthorized`, not an rxjs error — closing the editor on
          // that response would read as "saved" for a save that never happened, and the operator would only
          // find out on the next reload, when the previous (still current) credential's state comes back.
          if (response === LegalAccountState.CredentialsCorrect) {
            setting.isEditing = false;
          } else {
            this.globalAlertService.createAndShow("Fehler", CREDENTIALS_REJECTED_MESSAGE, AlertLevel.error, 8000);
          }
        },
        error: (error) => {
          console.error(error)
          // Same two structured refusals the switch has carried since #629 — a registered device or an open
          // cash point — now also reached from this save (#896). Branch on `error.code`, never render the
          // backend's English `description` (style guide, "Surfacing an API error").
          this.globalAlertService.createAndShow("Fehler",
            describeAdministrationError(error, CREDENTIALS_SAVE_FALLBACK), AlertLevel.error, 8000);
        }
      })
  }

  private load() {
    this.loadSettings();
  }

  private checkCredentials(type: LegalType) {
    let setting = this.settings.find(x => x.legalType == type);
    if (setting == undefined) return;

    this.settingsHttpService.isConfigured(type, this.shardId()).subscribe({
      next: (response) => {
        setting!.legalAccountState = response;
      },
      error: (error) => {
        setting!.legalAccountState = LegalAccountState.Unknown;
        this.globalAlertService.createAndShow("Fehler", "Accountdaten konnten nicht überprüft werden.", AlertLevel.error, 3000);
      }
    })
  }

  private loadSettings() {
    this.settingsHttpService.list(this.shardId()).subscribe({
      next: (response) => {
        this.settings = response;
        this.settings.forEach(x => this.checkCredentials(x.legalType));
      },
      error: (error) => {
        this.globalAlertService.createAndShow("Fehler", "Einstellungen konnten nicht geladen werden.", AlertLevel.error, 3000);
      }
    })
  }
}
